// The Terminal console's wire shape, and the RENDERER CONTRACT the five
// vocabulary-family modules implement (#4435, epic #4391).
//
// ─── WHY THE CONTRACT LIVES IN THE WIRE MODULE ───────────────────────────────
//
// `Renderer` is not a wire body, and a reader is right to ask what it is doing
// beside one. The alternative is a cycle: `entities/terminal/project.ts` imports
// the five family tables to compose them, so a contract declared THERE would be
// imported back by each family — five modules and their composer pointing at
// each other. Declared here, the graph is a fan-in with no edge back:
//
//   wire/terminal.ts  <-  renderers/{loop,dispatch,maintenance,commerce,review}.ts
//          ^                              |
//          |                              v
//          +---------------  entities/terminal/project.ts
//
// The renderer produces exactly one thing — a `TerminalEvent`'s renderable half —
// so the contract and the body it fills are one module's worth of subject.
//
// ─── `line` IS A KEY, NEVER A SENTENCE ───────────────────────────────────────
//
// Verified 2026-09-18, not assumed: `grep -rn '@developerz/i18n' apps/api
// apps/worker packages/domain` exits 1 — nothing on the producing side of this
// data can reach the catalogue, and this package imports nothing internal at all
// (`scripts/import-boundaries.test.ts` pins the `domain` row empty). A projector
// that returned a sentence would therefore be returning an ENGLISH sentence,
// permanently, to every locale. So the wire carries `{ key, params }` and the
// dashboard renders it through `t()`.
//
// ─── NO `org_id` ON THE WIRE ─────────────────────────────────────────────────
//
// The ROUTE is the scope. `listTerminalTailByAccount` fences on
// `audit_events.account_id = $1` and #4394's route derives that account from the
// caller's credential, so every row in a response is already one org's by
// construction. A tenant id in the body would be a second statement of the fence
// that a client could be tempted to filter on — and a client-side tenant filter
// is a fence that fails open the day it is wrong.
//
// ─── THE SAME-MILLISECOND TIEBREAK IS DOCUMENTED, NOT FIXED (from #4406) ─────
//
// `audit_events.id` is `uuid defaultRandom()` (`packages/db/src/schema/audit.ts:22`)
// and `ts` is precision-3, so two rows written inside one millisecond order
// RANDOMLY rather than causally, and this projection preserves whatever order it
// was handed. That is deliberate. Paging is total and correct because the keyset
// compares the `(ts, id)` PAIR, so any client-side reordering of a page would put
// the rendered list out of agreement with the cursor that produced it — dropping
// or repeating a row at the next page boundary. A cosmetically better order is
// not worth a paging bug; if causal order within a millisecond is ever needed, it
// needs a monotonic id at the WRITE, not a sort at the read.

import { z } from 'zod';

/**
 * A line's severity — a closed union, because it is what the console COLOURS by
 * and a widened one paints an unknown level as the default (usually "fine").
 *
 * `decision` is not a severity at all and is here on purpose: a policy gate, an
 * approval, a takeover are the rows an operator scans for, and folding them into
 * `info` makes them exactly as loud as a heartbeat.
 */
export type TerminalLevel = 'info' | 'ok' | 'warn' | 'fail' | 'decision';

/**
 * Who acted. Derived per #4435 step 5 — with ONE part deferred, stated here so
 * the gap is not read as an oversight.
 *
 * WHAT IS NOT DERIVED, AND WHY. Step 5 asks for the agent role to come from the
 * task envelope's sub-shape (`review` -> reviewer, `babysit` -> babysitter,
 * `class: interactive` -> helper, else coder), reusing the Fleet queue board's
 * classifier rather than copying it. The envelope is NOT on the row this
 * projection reads: `TerminalTailRow` (`packages/db/src/queries/terminal.ts:113`)
 * carries `id, ts, kind, tool, sessionId, userId, input, output,
 * visibleToMaintainer, repoFullName` and no task envelope, so there is nothing
 * here to classify. **No copy of that classifier was made** — the refinement is
 * deferred to whichever slice joins the task, and until then a renderer DECLARES
 * the actor its kind implies. That declaration is the honest half: `mcp` vs
 * native is already in the kind (`mcp_tool.call.*`), and a mail or cron lane is
 * `system` by construction.
 */
export type TerminalActor =
  | 'coder'
  | 'reviewer'
  | 'babysitter'
  | 'helper'
  | 'system'
  | 'human'
  | 'mcp';

/** What a line is ABOUT. Closed, because the console groups and links by it. */
export type TerminalSubjectKind =
  | 'task'
  | 'session'
  | 'repo'
  | 'pull_request'
  | 'issue'
  | 'plan'
  | 'box'
  | 'account'
  | 'key'
  | 'decision';

/**
 * The line's subject. `label` is the DISPLAY handle — a repo full name, a PR
 * number, a short task id — and is deliberately a plain string rather than
 * another key: it is data (`developerz-ai/developerz.ai`), not vocabulary, and
 * a locale does not translate a repo's name.
 *
 * THE RULE, found missing during #4435 review and stated here so #4394 inherits
 * a contract rather than reconstructing one per family: `id` IS a real id of
 * `kind` — never a foreign id (another entity's uuid or login standing in for
 * this one, the audit row's own uuid included) and never a fabricated sentinel
 * string ('unknown repo' is not a repo id). A renderer with no real id to report
 * for the kind it would otherwise pick either returns `null` (the row renders
 * nothing) or picks a DIFFERENT kind it does have a real id for — `sessionId`
 * is always present on `TerminalRow`, so `{ kind: 'session', id: row.sessionId,
 * label }` is the usual fallback, not `null`, when the row still has something
 * worth showing. `label` may be a human-readable placeholder ("unknown repo" as
 * a LABEL is fine); `id` may not be, because `id` is what the console groups
 * and links by.
 *
 * ENFORCED, not merely stated: `project.test.ts` walks every reachable
 * rendering kind under a null, a distinct-proxy and a withheld payload and
 * pins three things — `id` is never a payload-independent CONSTANT (the
 * collapsing class: `fleet_alert.*`, `integration.used`, `runner.release_pruned`,
 * `vault.reread`, `byok.provider_disagreement`), `id` is never `row.id` (the
 * non-collapsing class closed at #4445: `commerce.ts`'s `?? ctx.row.id`
 * fallbacks, `dispatch.escalated`, `maintenance.ts`'s `{kind:'task'|'box',
 * id: row.id}` sites, `maintenance-tenancy.ts`'s `{kind:'account'|'repo',
 * id: row.id}` sites), and an `id` equal to `row.sessionId` carries
 * `kind: 'session'` (`review.ts`'s `fleet.git_token.*`, formerly a `box`
 * wearing a session id). The class history: `review.ts`'s repo-subject helper
 * (round 1), `commerce.ts`'s `donation.*` subjects (round 2, `kind` and `id`
 * computed from disjoint sources), then the guard rather than a fourth
 * instance fix.
 */
export interface TerminalSubject {
  readonly kind: TerminalSubjectKind;
  readonly id: string;
  readonly label: string;
}

/**
 * The ids a line links out on. Every field corresponds to a route that EXISTS
 * (`apps/dashboard/src/lib/routes.ts`), which is the whole reason this is a
 * named bag and not a free-form map:
 *
 *   `runId`        -> `/fleet/runs/:run_id`
 *   `planId`       -> `/fleet/plans/:plan_id`   (a task's fallback when no run)
 *   `sessionId`    -> `/audit/:session_id`
 *   `repoFullName` -> `/repos/:full_name/pending`
 *   `blockerId`    -> `/blockers`
 *
 * There is NO standalone task, PR, issue or box route today. `taskId`,
 * `prNumber`, `issueNumber` and `boxId` still ride here because they are what a
 * line is about and what an operator greps for — they are refs, not links, and
 * a consumer that invents a `/tasks/:id` or `/issues/:n` href from one is
 * inventing a 404.
 *
 * Every field is `?: T | undefined`, not a bare `?:`. `exactOptionalPropertyTypes`
 * is on in this repo, so the two differ: a bare `?:` forbids the key from being
 * PRESENT-AND-UNDEFINED, which is exactly what a renderer produces when it
 * spreads a conditional ref. The union admits that shape instead of pushing all
 * five families into a `...(x ? { x } : {})` dance.
 */
export interface TerminalRefs {
  readonly taskId?: string | undefined;
  readonly runId?: string | undefined;
  readonly planId?: string | undefined;
  readonly sessionId?: string | undefined;
  readonly repoFullName?: string | undefined;
  readonly prNumber?: number | undefined;
  readonly issueNumber?: number | undefined;
  readonly boxId?: string | undefined;
  readonly blockerId?: string | undefined;
}

/**
 * A line's i18n key, namespaced so one grep finds the whole catalogue and a
 * renderer cannot accidentally point at an unrelated key.
 *
 * It is a TEMPLATE LITERAL rather than the closed union of every key because
 * the families own their keys: a closed union here would have to be edited by
 * five agents at once, which is the merge conflict the file split exists to
 * avoid. The tightening that IS worth having is catalogue COVERAGE — every key a
 * renderer emits has an `en` entry — and that belongs beside the catalogue, in
 * the dashboard, where both sides are visible.
 */
export type TerminalLineKey = `terminal.${string}`;

/**
 * Interpolation values. `string | number` only: a boolean has no locale-neutral
 * rendering ("true" is not a word in any UI), and a nested object would be a
 * sentence being assembled one layer down from where it is translated.
 */
export type TerminalLineParams = Readonly<Record<string, string | number>>;

/** The renderable half of a line: a key the dashboard resolves, plus its values. */
export interface TerminalLine {
  readonly key: TerminalLineKey;
  readonly params: TerminalLineParams;
}

/**
 * One rendered Terminal line.
 *
 * `has_raw` carries `audit_events.visible_to_maintainer`, and what it means is
 * narrower than it looks: the payload of an operator-only row is withheld in the
 * SELECT (`buildTerminalTailQuery`), so there is nothing downstream for this flag
 * to gate. It tells a client whether an expansion is OFFERED — not whether one is
 * permitted — which is the difference between a UI that shows a dead "expand"
 * affordance and one that does not.
 */
export interface TerminalEvent {
  readonly id: string;
  readonly ts: string;
  readonly level: TerminalLevel;
  readonly actor: TerminalActor;
  readonly subject: TerminalSubject;
  readonly line: TerminalLine;
  readonly refs: TerminalRefs;
  /**
   * The chain a line belongs to — the audit session id. Nullable because a
   * consumer must not assume grouping is always available; it is not derived
   * from anything a renderer chooses, so two lines of one session always agree.
   */
  readonly correlation_id: string | null;
  readonly has_raw: boolean;
}

/**
 * `GET /v1/terminal/events`' own `per_page` ceiling — strictly below the db
 * ceiling (`TERMINAL_TAIL_MAX_LIMIT`, `packages/db/src/queries/terminal.ts`,
 * 500) so the route's `limit + 1` `hasMore` probe never clamps back down to
 * `limit` and silently reports a full page as "no more" (#4394 review).
 *
 * Declared HERE, not re-typed in `apps/api/src/routes/terminal.ts` or
 * `apps/dashboard/src/lib/terminal-events.ts` — both import this one constant
 * (`apps/dashboard` cannot import `apps/api`, and this package is the one home
 * both sides already reach for the wire type from). A route ceiling and a
 * client page-size independently declaring the same number is exactly the
 * drift that shipped a 400 on every backfill request (#4395 review): the
 * client asked for 500, the route only ever allowed 200.
 */
export const TERMINAL_EVENTS_MAX_PAGE_SIZE = 200;

/**
 * The published body. Zod here is deliberately THIN — `line.key` and the string
 * unions go through `z.custom`/`z.enum` so `z.infer` stays byte-identical to the
 * hand-written types above rather than widening them to `string`.
 *
 * It exists for #4394's OpenAPI document, not as a trust boundary: the only
 * producer is `projectTerminalEvent` in this package, and validating our own
 * pure function's output on every row would be a second declaration free to
 * drift from the first.
 */
export const TerminalEventSchema: z.ZodType<TerminalEvent> = z.object({
  id: z.string(),
  ts: z.string(),
  level: z.enum(['info', 'ok', 'warn', 'fail', 'decision']),
  actor: z.enum(['coder', 'reviewer', 'babysitter', 'helper', 'system', 'human', 'mcp']),
  subject: z.object({
    kind: z.enum([
      'task',
      'session',
      'repo',
      'pull_request',
      'issue',
      'plan',
      'box',
      'account',
      'key',
      'decision',
    ]),
    id: z.string(),
    label: z.string(),
  }),
  line: z.object({
    // A regex-constrained `ZodString`, not `z.custom` — the same runtime check
    // ("starts with `terminal.`"), but representable in JSON Schema. `z.custom`
    // is a validator function only, and `apps/api/src/openapi.ts`'s
    // `componentSchema` calls `z.toJSONSchema` with no `unrepresentable`
    // override, so a `z.custom` field THROWS at OpenAPI-document build time
    // rather than publishing — found registering this schema for #4394. The
    // cast is the same one `z.custom` needed: nothing here can express the
    // template-literal type structurally, so the schema states the pattern and
    // the cast states the brand.
    key: z.string().regex(/^terminal\./) as unknown as z.ZodType<TerminalLineKey>,
    params: z.record(z.string(), z.union([z.string(), z.number()])),
  }),
  refs: z.object({
    taskId: z.string().optional(),
    runId: z.string().optional(),
    planId: z.string().optional(),
    sessionId: z.string().optional(),
    repoFullName: z.string().optional(),
    prNumber: z.number().optional(),
    issueNumber: z.number().optional(),
    boxId: z.string().optional(),
    blockerId: z.string().optional(),
  }),
  correlation_id: z.string().nullable(),
  has_raw: z.boolean(),
});

// ─── the row a renderer reads ────────────────────────────────────────────────

/**
 * The audit row this projection reads, declared HERE rather than imported.
 *
 * `@developerz/domain` imports nothing internal — that is the boundary
 * `scripts/import-boundaries.test.ts` pins — so it cannot take
 * `TerminalTailRow` from `@developerz/db`. This is its structural mirror:
 * `listTerminalTailByAccount`'s rows satisfy it by shape, with no cast at
 * #4394's seam, and a drift between the two is a plain TS2345 there.
 *
 * `input` and `output` are `unknown`, exactly as the query declares them, and
 * that is load-bearing rather than lazy. They are NULL on any row whose
 * `visible_to_maintainer` is false — withheld by a `case when` in the SELECT,
 * not by a mapper — so a renderer narrowing one must handle the withheld case,
 * and a narrower declared type here would be a promise the query does not keep.
 */
export interface TerminalRow {
  readonly id: string;
  readonly ts: Date;
  readonly kind: string;
  readonly tool: string | null;
  readonly sessionId: string;
  readonly userId: string | null;
  readonly input: unknown;
  readonly output: unknown;
  readonly visibleToMaintainer: boolean;
  readonly repoFullName: string | null;
}

// ─── the run-event row a verbose renderer reads ──────────────────────────────

/**
 * The `task_run_events` row this projection reads — declared HERE rather than
 * imported, for the same reason {@link TerminalRow} is: `@developerz/domain`
 * imports nothing internal (`scripts/import-boundaries.test.ts`), so it cannot
 * take it from `@developerz/db` or from the closed `TaskRunEvent` schema in
 * `../value-objects/task-run-event/row.ts`. A drift between the two is a plain
 * TS2345 at the slice that joins them — `entity/runner/projection`, eventually.
 *
 * `kind` is `string` (the raw text column) for the same reason `TerminalRow.kind`
 * is: the closed `TaskRunEventKindSchema` is in `value-objects/task-run-event/kind.ts`
 * and validating one against it is the parser's job, the row's contract here is
 * shape only. `payload` is `unknown`: it narrows on `kind` (`ToolCallPayload`,
 * `StatusPayload`, `RunFinishedPayload`, `RunReleasedPayload`, …), and a narrower
 * declared type would be a promise this row does not keep. `prevHash` is the
 * hash-chain link this run's chain carries (`packages/db/src/schema/task-run-events.ts`'s
 * module note); null on the first row. `seq` is the monotonic insertion cursor
 * that backs the chain head — present on every row by `bigserial`. `runId` is a
 * plain string (no FK, runs live only as a column on `tasks`, `schema/tasks.ts`).
 */
export interface TaskRunEventRow {
  readonly id: string;
  readonly seq: number;
  readonly runId: string;
  readonly taskId: string;
  readonly ts: Date;
  readonly kind: string;
  readonly payload: unknown | null;
  readonly prevHash: string | null;
}

// ─── attempt N of M ──────────────────────────────────────────────────────────

/**
 * Where a failure sits on the retry ladder — a discriminated union, with the
 * refusals NAMED, because the honest answer today is usually not "N of M".
 *
 * MEASURED against the writers, 2026-09-18. There are three sites that write a
 * `task.failed` / `task.reenqueued` row and they do not agree:
 *
 *   `apps/worker/src/wiring/nats-consumers.ts:805` — `output.attempt` IS the
 *     attempt number, and `visibleToMaintainer` is true on the `failed` arm, so
 *     it survives the SELECT. This is the `nth` case.
 *   `apps/worker/src/cron/task-ending-audit.ts:81` — `output` is the lane's
 *     opaque `result: Record<string, unknown>`; an attempt is there only if that
 *     lane happened to put one there.
 *   `apps/worker/src/dispatch/released-claims.ts#recordReleasedClaims` — the presence reaper, whose
 *     `output` is `{ status: 'pending' }`. No attempt at all: `not_recorded`.
 *
 * And M IS NOWHERE ON THIS PATH. The ceiling lives on the task's BUDGET
 * (`TaskBudgetKind.Charged`'s `maxAttempts`, default `TASK_MAX_ATTEMPTS`), which
 * the terminal row does not carry. Substituting the default constant would be a
 * lie for every task with a non-default budget — `maxAttempts: 1` tasks exist —
 * and a wrong "3 of 3" is worse than an honest "3". So `known` is declared and
 * currently UNINHABITED: it becomes reachable the moment a slice joins the task
 * budget, and until then the type says which half we have rather than pretending.
 */
export type TerminalAttempt =
  /** Both numbers. Not produced today — see the module note above. */
  | { readonly kind: 'known'; readonly n: number; readonly of: number }
  /** The attempt number, with no ceiling on the row to compare it against. */
  | { readonly kind: 'nth'; readonly n: number }
  /** No number at all, and which absence it is. */
  | { readonly kind: 'unknown'; readonly reason: 'payload_withheld' | 'not_recorded' };

/** The two interpolation values every `fail` line carries. */
export interface TerminalAttemptParams {
  readonly attempt: string;
  readonly of: string;
}

/**
 * The ONE rendering of a `TerminalAttempt` into line params.
 *
 * Exported so the five families share it rather than each choosing a glyph for
 * "we do not know" — five choices would be five different consoles. The absent
 * value is the token `unknown` and not `?` or an em dash: a glyph chosen here is
 * a display decision made in a package that cannot reach the catalogue, and the
 * dashboard's `en` entry is where it belongs.
 */
export function attemptParams(a: TerminalAttempt): TerminalAttemptParams {
  if (a.kind === 'known') return { attempt: String(a.n), of: String(a.of) };
  if (a.kind === 'nth') return { attempt: String(a.n), of: 'unknown' };
  return { attempt: 'unknown', of: 'unknown' };
}

// ─── the renderer contract ───────────────────────────────────────────────────

/** What every renderer is handed: the row, and the kind already parsed off it. */
export interface TerminalRenderContext {
  readonly row: TerminalRow;
}

/**
 * What a `fail` renderer is handed. The extra field is the point of the union
 * below: a failure line that cannot say which attempt it was is the line an
 * operator most often needs, so the ladder position is a REQUIRED input rather
 * than something a renderer may go looking for in the payload.
 */
export interface TerminalFailRenderContext extends TerminalRenderContext {
  readonly attempt: TerminalAttempt;
}

/** What a renderer returns. `null` means this particular ROW renders nothing. */
export interface TerminalRendered {
  readonly line: TerminalLine;
  readonly subject: TerminalSubject;
  readonly refs: TerminalRefs;
}

/**
 * What a `fail` renderer returns — the same, with the attempt params REQUIRED in
 * `line.params`.
 *
 * This is the half that makes the constraint bite. A required input alone can be
 * received and ignored (TypeScript's method parameters are bivariant, so a
 * renderer declaring the base context still type-checks); requiring the numbers
 * on the way OUT means the value has to travel THROUGH the function. Call
 * {@link attemptParams} and spread it — that is the intended and only shape.
 */
export interface TerminalFailRendered {
  readonly line: {
    readonly key: TerminalLineKey;
    readonly params: TerminalLineParams & TerminalAttemptParams;
  };
  readonly subject: TerminalSubject;
  readonly refs: TerminalRefs;
}

interface TerminalRendererShape<L extends TerminalLevel | 'by_row', C, R> {
  /**
   * The line's severity, declared per KIND rather than computed per row.
   *
   * The vocabulary already splits outcomes into separate kinds — `task.failed`
   * and `task.completed` are two kinds, not one kind with a result — so a
   * per-row level would be a second severity axis nothing needs. The ONE kind
   * that genuinely varies earns the `by_row` arm below: `task.moved` (#6768), a
   * settled move item whose row says whether it LANDED or FAILED (one row shape
   * the issue fixed: `{ task_id, batch_id, from, to, state, failure }`), so its
   * level is the settlement's and its renderer answers it per row.
   */
  readonly level: L;
  /** The actor this kind implies. Overridden to `human` when the row has a user. */
  readonly actor: TerminalActor;
  readonly render: (ctx: C) => R | null;
}

/**
 * One kind's renderer. A discriminated union on `level`, so the `fail` arm — and
 * only the `fail` arm — is handed the attempt and must carry it back out.
 */
export type TerminalRenderer =
  | TerminalRendererShape<'info', TerminalRenderContext, TerminalRendered>
  | TerminalRendererShape<'ok', TerminalRenderContext, TerminalRendered>
  | TerminalRendererShape<'warn', TerminalRenderContext, TerminalRendered>
  | TerminalRendererShape<'decision', TerminalRenderContext, TerminalRendered>
  | TerminalRendererShape<'fail', TerminalFailRenderContext, TerminalFailRendered>
  /**
   * The level is the ROW's, answered with the line (never `fail`: a per-row level carries
   * no attempt ladder). One kind uses it, `task.moved`; a second must argue it the same way.
   */
  | TerminalRendererShape<
      'by_row',
      TerminalRenderContext,
      TerminalRendered & { readonly level: Exclude<TerminalLevel, 'fail'> }
    >;

/**
 * A kind's decision: render it, or leave it out of the console.
 *
 * `'omit'` is a DECISION and not an absence — that is the whole mechanism. A
 * missing key is a type error; an omitted one is a person having looked at the
 * kind and said no, in a diff a reviewer can read.
 */
export type TerminalRendererEntry = TerminalRenderer | 'omit';

/** One family's slice of the table. Keys are audit kinds; see the helper below. */
export type TerminalRendererGroup = Readonly<Record<string, TerminalRendererEntry>>;

/**
 * How a family DECLARES its group. Use this — never a type annotation.
 *
 * The footgun it removes is specific and fatal to this whole slice. The
 * composition in `entities/terminal/project.ts` is exhaustive only because
 * TypeScript knows each group's LITERAL keys. Writing
 * `const G: Partial<Record<AuditEventKind, …>> = { … }` erases them — the spread
 * then contributes "possibly any of them", the composed table fails to satisfy
 * `Record<AuditEventKind, …>` for every kind at once, and the natural "fix" for
 * a wall of errors is to widen the key type to `string`, at which point nothing
 * is checked at all and the slice has shipped its own defeat.
 *
 * The `const` type parameter is equally load-bearing: without it `level: 'fail'`
 * infers as `string` and the discriminated union above stops discriminating.
 *
 * Five call sites today, one per family, so this clears the house rule against
 * abstraction with no consumer.
 */
export function terminalRendererGroup<const G extends TerminalRendererGroup>(group: G): G {
  return group;
}

// ─── the verbose renderer's contract ─────────────────────────────────────────
//
// Mirrors the audit renderer's contract above, with two DELIBERATE DIFFERENCES:
//
//   1. The row IS `TaskRunEventRow` rather than `TerminalRow` (audit row). The
//      verbose family lives behind the same console, the same `TerminalLevel`
//      palette, and the same i18n key shape — the contract that is portable is
//      portable; the rest is row-specific.
//
//   2. NO `fail` ARM. A run-event kind does not name a task-attempt ladder —
//      `task_run_events` is per `run_id` and a `fail` line here has nothing to
//      be `n of m` about. The audit-side `attempt` plumbing is silenced by an
//      absent arm rather than spelled a second time here as an unused union
//      member: a caller that accidentally writes `level: 'fail'` over a
//      `TaskRunEventRendererEntry` gets a clean TS2353, and not a render that
//      drops the ladder, which would be the worst outcome (a `fail` line with
//      empty attempt params).
//
// And ONE ABSENT OVERRIDE: `TerminalRow.actor` is corrected to `'human'` at the
// audit projection when the row carries a `userId`. `TaskRunEventRow` carries
// NO `userId` (these rows are written by workers/runners, never by a session
// user, `schema/task-run-events.ts`), so the `actor` declared on a verbose entry
// is the actor as rendered — no seam needed, none added.

/**
 * What every verbose renderer is handed: the row and, when the parser ran
 * successfully, the kind already parsed off it. The parser's gate is the
 * validator at `value-objects/task-run-event/kind`'s `TaskRunEventKindSchema`,
 * NOT here — keeping this row-shaped only is the same stance {@link TerminalRow}
 * takes.
 */
export interface TaskRunEventRenderContext {
  readonly row: TaskRunEventRow;
}

/**
 * What a verbose renderer returns. `null` means this particular ROW renders
 * nothing — `run_heartbeat` is the one a row of which returns null, because
 * its discriminator (`payload.event = 'run_heartbeat'` inside a `status`-kind
 * row, `RUN_HEARTBEAT_EVENT` in `value-objects/task-run-event/row.ts`) is the
 * row-only-NULL the verbose projection must surface.
 */
export interface TaskRunEventRendered {
  readonly line: TerminalLine;
  readonly subject: TerminalSubject;
  readonly refs: TerminalRefs;
}

interface TaskRunEventRendererShape<L extends TerminalLevel, C, R> {
  /**
   * The line's severity, declared per KIND rather than computed per row. Same
   * rationale as the audit shape: the vocabulary already splits outcomes into
   * separate kinds (`run_finished` is the happy terminal, `run_released` is the
   * box-gave-it-back one, `error` is the run-time exception), so a per-row level
   * would be a second severity axis nothing needs.
   */
  readonly level: L;
  /** The actor this kind implies. */
  readonly actor: TerminalActor;
  readonly render: (ctx: C) => R | null;
}

/**
 * One task-run-event kind's renderer. A four-arm union — `info` / `ok` / `warn`
 * / `decision` — and deliberately NOT five: a `fail` entry would land on a row
 * whose shape (per-run, no `attempt`) has nothing to ladder against. The audit
 * renderer's `fail` plumbing is therefore absent here, not duplicated-and-dead.
 */
export type TaskRunEventRenderer =
  | TaskRunEventRendererShape<'info', TaskRunEventRenderContext, TaskRunEventRendered>
  | TaskRunEventRendererShape<'ok', TaskRunEventRenderContext, TaskRunEventRendered>
  | TaskRunEventRendererShape<'warn', TaskRunEventRenderContext, TaskRunEventRendered>
  | TaskRunEventRendererShape<'decision', TaskRunEventRenderContext, TaskRunEventRendered>;

/**
 * A kind's decision: render it, or leave it out of the console.
 *
 * `'omit'` is a DECISION and not an absence — same mechanism the audit renderer
 * family uses, and the same one this file documents three times already. A
 * missing key is a type error; an omitted one is a person having looked at the
 * kind and said no, in a diff a reviewer can read.
 */
export type TaskRunEventRendererEntry = TaskRunEventRenderer | 'omit';

/** One verbose family's slice. Keys are task-run-event kinds. */
export type TaskRunEventRendererGroup = Readonly<Record<string, TaskRunEventRendererEntry>>;

/**
 * How a verbose family DECLARES its group. Use this — never a type annotation.
 *
 * Same footgun as {@link terminalRendererGroup}: the `const` type parameter is
 * load-bearing — without it, `level: 'warn'` infers as `string` and the
 * discriminated union above stops discriminating. The composed verbose table
 * will be merged into a wider projection by the verbose slice's own composer
 * (sibling of `entities/terminal/project.ts`), and that merger is exhaustive
 * only because each group declares literal keys.
 */
export function taskRunEventRendererGroup<const G extends TaskRunEventRendererGroup>(group: G): G {
  return group;
}
